import { FormEditorTypes } from "@/components/forms/form-editor.config.ts";
import { FormEditor } from "@/components/forms/FormEditor.tsx";
import { Search } from "@/components/search/Search.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { SurveyEditSidebar } from "@/pages/talent/surveys/edit/components/SurveyEditSidebar.tsx";
import {
  SurveyEditPageProps,
  useSurveyEditPage,
} from "@/pages/talent/surveys/edit/pages/survey-edit-pages.tsx";
import { SurveyEditTitleSlot } from "@/pages/talent/surveys/edit/pages/SurveyEditTitleSlot.tsx";
import { SurveyEditPageStep } from "@/pages/talent/surveys/edit/survey-edit.ts";
import { useSurveyTemplateSearch } from "@/queries/surveys/use-survey-templates.query.ts";
import { useSurveyUpdateForm } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFormTemplateFilter } from "@/types/forms/form-template.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  FullScreenPage,
  Radio,
  Skeleton,
  Stack,
  Switch,
  Title,
} from "@skailer-com/uikit";
import { round } from "lodash";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

export const SurveyEditQuestionsPage = ({ survey }: SurveyEditPageProps) => {
  const { t } = useTranslation();

  const { confirmExit, navigateToStep } = useSurveyEditPage();

  const surveyUpdate = useSurveyUpdateForm();

  const { canIView } = usePermissions();

  const shouldShowInitialScreen = !survey.form_id && !survey.form_template_id;

  const [isCustomForm, setIsCustomForm] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);

  const [templateSearchKey, setTemplateSearchKey] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<ApiFormTemplateFilter>({
    page_number: 1,
    text: undefined,
  });

  const [syncedFormId, setSyncedFormId] = useState(survey.form_id);
  if (survey.form_id !== syncedFormId) {
    setSyncedFormId(survey.form_id);
    setIsCustomForm(false);
  }

  useDebounce(
    () => {
      setFilter({ ...filter, text: templateSearchKey });
    },
    300,
    [templateSearchKey]
  );

  const { data: templates, isFetching } = useSurveyTemplateSearch(
    filter,
    canIView(ApiPermission.survey_templates)
  );

  const [syncedTemplates, setSyncedTemplates] = useState<typeof templates>(undefined);
  if (templates !== syncedTemplates) {
    setSyncedTemplates(templates);
    if (templates && templates.items.length > 0) {
      setSelectedTemplateId(templates.items[0].id);
    } else {
      setSelectedTemplateId(undefined);
    }
  }

  const handleSelectFormOrTemplate = async () => {
    if (!shouldShowInitialScreen) {
      navigateToStep(survey.id, SurveyEditPageStep.launch);
      return;
    }
    if (isCustomForm) {
      try {
        await surveyUpdate.mutateAsync({
          id: survey.id,
          payload: {
            create_new_form: true,
          },
        });

        analyticsService.trackEvent(analyticEvents.surveys.edited, {
          [analyticProperties.id]: survey.id,
          [analyticProperties.tab]: SurveyEditPageStep.questions,
        });
      } catch {
        return;
      }
    } else if (selectedTemplateId) {
      try {
        await surveyUpdate.mutateAsync({
          id: survey.id,
          payload: {
            form_template_id: selectedTemplateId,
          },
        });

        analyticsService.trackEvent(analyticEvents.surveys.edited, {
          [analyticProperties.id]: survey.id,
          [analyticProperties.tab]: SurveyEditPageStep.questions,
        });
      } catch {
        return;
      }
    }
  };

  const renderContent = () => {
    if (shouldShowInitialScreen) {
      return (
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title
              header={t("surveys:select_template_title")}
              caption={t("surveys:select_template_description")}
              size={"lg"}
            />
            <Switch
              checked={isCustomForm}
              onChange={() => {
                setIsCustomForm(!isCustomForm);
              }}
              title={t("surveys:start_from_scratch_title")}
              description={t("surveys:start_from_scratch_description")}
            />
            {!isCustomForm && (
              <>
                <Box>
                  <Search
                    placeholder={t("common:search")}
                    value={templateSearchKey}
                    onInput={(e) => {
                      setTemplateSearchKey(e.currentTarget.value);
                    }}
                    type={"text"}
                  />
                </Box>
                {isFetching && <Skeleton className={"w-full h-10"} />}
                {templates && templates.items.length > 0 && !isFetching && (
                  <Stack gap={"sm"}>
                    {templates.items.map((template) => (
                      <Fragment key={`template-${template.id}`}>
                        <Radio
                          title={template.title}
                          checked={selectedTemplateId === template.id}
                          onChange={() => {
                            setSelectedTemplateId(template.id);
                          }}
                          caption={`${t("plurals:question_count", { count: template.total_questions })} · ${t(
                            "plurals:plural_minutes",
                            {
                              count: round(template.total_estimated_time_seconds / 60),
                            }
                          )}`}
                        />
                        <hr className={"ml-8"} />
                      </Fragment>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>

          <Button
            size={"lg"}
            isLoading={surveyUpdate.isPending}
            onClick={handleSelectFormOrTemplate}
          >
            {t("common:continue")}
          </Button>
        </Stack>
      );
    }

    return (
      <>
        <Stack gap={"lg"} justify={"center"}>
          {(survey.form_id || survey.form_template_id) && (
            <FormEditor type={FormEditorTypes.survey} id={survey.id} />
          )}
        </Stack>
      </>
    );
  };

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(survey.id)}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={<SurveyEditTitleSlot step={SurveyEditPageStep.questions} survey={survey} />}
      isCenterVertical={false}
      contentSize={shouldShowInitialScreen ? "sm" : "md"}
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(survey.id, SurveyEditPageStep.details);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={handleSelectFormOrTemplate}>
            {t("common:continue")}
          </Button>
        </>
      }
      sidebarSlot={<SurveyEditSidebar step={SurveyEditPageStep.questions} survey={survey} />}
    >
      {renderContent()}
    </FullScreenPage>
  );
};
